'use client';

import { c as _c } from "react/compiler-runtime";
import { reaction } from 'mobx';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { ProjectOpenRequest, UpdaterState } from '@/app/sdk';
import type { SdkCompatibilityMode } from '@/app/utils/versionUtils';
import { appPreferencesStore } from '@/app/global/appPreferencesStore';
import { globalStore } from '@/app/globalStore';
import { initSdk, sdk } from '@/app/sdk';
import { getRuntimeCompatibility, MINIMUM_SDK_AUTO_UPDATE_VERSION, MINIMUM_SDK_VERSION } from '@/app/utils/versionUtils';
import { ENGINE_VERSION, PRODUCT_NAME, SKIP_SDK_VERSION_CHECK } from '@/const';
import { handleExternalProjectOpen, setPlayModeActive, verifyProjectPath } from '@/editor/core/actions/editorActions';
import { installEditorBridge } from '@/editor/core/editor-bridge';
import { project, sceneStore } from '@/editor/store';
import { applyUpdaterStateWithToasts } from '@/editor/updater/updaterStateTransitions';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { requestSentryNextjsSdkVersionTag } from '@/lib/sentry/applySentryVersionTags';
import { cleanStackTrace } from '@/lib/utils/cleanStackTrace';

// Global initialization state
let isGloballyInitialized = false;
let isInitializing = false;
let initPromise: Promise<void> | null = null;
const initCallbacks = new Set<(isInitialized: boolean) => void>();

// Version check state
let isVersionValid = true;
let currentSdkVersion = '';
let currentSdkEngineVersion = '';
const versionCheckCallbacks = new Set<(isVersionValid: boolean, currentVersion: string) => void>();
export type SdkCompatibilityReason = 'none' | 'runtimeVersionTooOld' | 'engineVersionTooOld' | 'engineVersionUnknown' | 'belowManualFloor' | 'updaterUnavailable' | 'updaterFailed' | 'noCompatibleUpdateFound' | 'updaterReportedError' | 'unknownVersion';
let sdkCompatibilityMode: SdkCompatibilityMode = 'compatible';
let sdkCompatibilityReason: SdkCompatibilityReason = 'none';
const sdkCompatibilityCallbacks = new Set<(mode: SdkCompatibilityMode, reason: SdkCompatibilityReason) => void>();

// Global cleanup functions
let globalRemoveLoadingOverlayListener: (() => void) | null = null;
let globalRemoveDeepLinkListener: (() => void) | null = null;
let globalRemoveProjectOpenListener: (() => void) | null = null;
let globalRemoveLogListener: (() => void) | null = null;
let globalRemoveWarnListener: (() => void) | null = null;
let globalRemoveErrorListener: (() => void) | null = null;
let globalRemovePlayModeLogListener: (() => void) | null = null;
let globalRemovePlayModeWarnListener: (() => void) | null = null;
let globalRemovePlayModeErrorListener: (() => void) | null = null;
let globalRemovePlayModeFatalErrorListener: (() => void) | null = null;
let globalRemovePlayModeFatalOverlayDismissedListener: (() => void) | null = null;
let globalRemovePlayModeWindowClosedListener: (() => void) | null = null;
let globalRemoveUpdaterStateListener: (() => void) | null = null;
/** Polls main-process updater state while download/check is in progress (heals missed IPC). */
let updaterStatePollInterval: ReturnType<typeof setInterval> | null = null;
let updaterStatePollDeadline: number | null = null;
let isHandlingFatalOverlayDismiss = false;
let latestSafeNavigate: ((path: string) => Promise<void>) | null = null;
const warnedSdkCompatibilityFeatures = new Set<string>();
let projectOpenProcessing = false;
let queuedProjectOpenRequest: ProjectOpenRequest | null = null;
type ListenerCallback = (...args: unknown[]) => void;
type ListenerUnsubscribe = () => void;
const REQUIRED_UPDATE_PROGRESS_TIMEOUT_MS = 8000;
const UPDATER_STATE_POLL_INTERVAL_MS = 2000;
const UPDATER_STATE_POLL_MAX_MS = 30 * 60 * 1000;
function warnSdkCompatibilityOnce(featurePath: string, details: string): void {
  if (warnedSdkCompatibilityFeatures.has(featurePath)) {
    return;
  }
  warnedSdkCompatibilityFeatures.add(featurePath);
  console.warn(`[App] SDK compatibility: ${featurePath} ${details}`);
}
function getSdkCallableMethod(owner: unknown, methodName: string, featurePath: string): ((...args: unknown[]) => unknown) | null {
  if (!owner || typeof owner !== 'object' && typeof owner !== 'function') {
    warnSdkCompatibilityOnce(featurePath, 'is unavailable, continuing in degraded mode');
    return null;
  }
  const method = (owner as Record<string, unknown>)[methodName];
  if (typeof method !== 'function') {
    warnSdkCompatibilityOnce(featurePath, 'is unavailable, continuing in degraded mode');
    return null;
  }
  return method as (...args: unknown[]) => unknown;
}
function registerListenerIfAvailable(owner: unknown, methodName: string, featurePath: string, callback: ListenerCallback): ListenerUnsubscribe | null {
  const register = getSdkCallableMethod(owner, methodName, featurePath);
  if (!register) {
    return null;
  }
  const unsubscribe = register.call(owner, callback);
  if (typeof unsubscribe !== 'function') {
    warnSdkCompatibilityOnce(featurePath, 'did not return an unsubscribe function, continuing in degraded mode');
    return null;
  }
  return unsubscribe as ListenerUnsubscribe;
}
function ensureSdkStartupCapabilities(currentSdk: NonNullable<typeof sdk>): void {
  getSdkCallableMethod(currentSdk.playMode, 'onFatalOverlayDismissed', 'playMode.onFatalOverlayDismissed');
  getSdkCallableMethod(currentSdk.playMode, 'onWindowClosed', 'playMode.onWindowClosed');
  getSdkCallableMethod(currentSdk.app, 'onUpdateStateChange', 'app.onUpdateStateChange');
  getSdkCallableMethod(currentSdk.app, 'getUpdateState', 'app.getUpdateState');
  getSdkCallableMethod(currentSdk.projectOpen, 'onRequest', 'projectOpen.onRequest');
  getSdkCallableMethod(currentSdk.projectOpen, 'reportResult', 'projectOpen.reportResult');
}
async function syncUpdaterStateFromSdkSafely(currentSdk: NonNullable<typeof sdk>): Promise<void> {
  if (globalStore.isUpdaterMockEnabled) {
    return;
  }
  const getUpdateState = getSdkCallableMethod(currentSdk.app, 'getUpdateState', 'app.getUpdateState');
  if (!getUpdateState) {
    return;
  }
  try {
    const nextState = (await Promise.resolve(getUpdateState.call(currentSdk.app))) as UpdaterState;
    applyUpdaterStateWithToasts(nextState);
    reconcileSdkCompatibilityWithUpdaterState(nextState);
  } catch (error) {
    console.warn('[App] SDK compatibility: failed to fetch updater state, continuing in degraded mode', error);
  }
}
function shouldPollUpdaterPhase(phase: UpdaterState['phase']): boolean {
  return phase === 'checking' || phase === 'available' || phase === 'downloading';
}
function stopUpdaterStatePolling(): void {
  if (updaterStatePollInterval !== null) {
    clearInterval(updaterStatePollInterval);
    updaterStatePollInterval = null;
  }
  updaterStatePollDeadline = null;
}

/** Starts a single polling interval while phase is transitional; no-op if already polling or phase does not need polls. */
function ensureUpdaterStatePollingForSdk(currentSdk: NonNullable<typeof sdk>): void {
  if (globalStore.isUpdaterMockEnabled || !shouldPollUpdaterPhase(globalStore.updaterPhase)) {
    stopUpdaterStatePolling();
    return;
  }
  if (updaterStatePollInterval !== null) {
    return;
  }
  updaterStatePollDeadline = Date.now() + UPDATER_STATE_POLL_MAX_MS;
  updaterStatePollInterval = setInterval(() => {
    if (globalStore.isUpdaterMockEnabled) {
      stopUpdaterStatePolling();
      return;
    }
    if (updaterStatePollDeadline !== null && Date.now() > updaterStatePollDeadline) {
      stopUpdaterStatePolling();
      return;
    }
    void syncUpdaterStateFromSdkSafely(currentSdk).then(() => {
      if (!shouldPollUpdaterPhase(globalStore.updaterPhase)) {
        stopUpdaterStatePolling();
      }
    });
  }, UPDATER_STATE_POLL_INTERVAL_MS);
}
function setSdkCompatibilityState(mode: SdkCompatibilityMode, reason: SdkCompatibilityReason): void {
  sdkCompatibilityMode = mode;
  sdkCompatibilityReason = reason;
  sdkCompatibilityCallbacks.forEach(callback => callback(mode, reason));
}

/** True when the SDK updater is actively checking, downloading, or ready to install. */
export function isUpdaterRecoveryInProgress(phase: UpdaterState['phase'] = globalStore.updaterPhase): boolean {
  return phase === 'checking' || phase === 'available' || phase === 'downloading' || phase === 'readyToInstall';
}

/**
 * Align compatibility gate with live updater state (e.g. manual fallback → in-app recovery).
 * Does not mark the runtime as compatible until startup version checks pass after restart.
 */
function reconcileSdkCompatibilityWithUpdaterState(state: UpdaterState): void {
  if (globalStore.isUpdaterMockEnabled || isVersionValid) {
    return;
  }
  if (isUpdaterRecoveryInProgress(state.phase)) {
    if (sdkCompatibilityMode === 'manualUpdateRequired') {
      const waitingReason = sdkCompatibilityReason === 'none' || sdkCompatibilityReason === 'updaterUnavailable' || sdkCompatibilityReason === 'updaterFailed' || sdkCompatibilityReason === 'noCompatibleUpdateFound' || sdkCompatibilityReason === 'updaterReportedError' ? 'runtimeVersionTooOld' : sdkCompatibilityReason;
      setSdkCompatibilityState('updateRequiredWaiting', waitingReason);
    }
  }
}
function hasRequiredAutoUpdateCapabilities(currentSdk: NonNullable<typeof sdk>): boolean {
  const hasCheckForUpdates = !!getSdkCallableMethod(currentSdk.app, 'checkForUpdates', 'app.checkForUpdates');
  const hasGetUpdateState = !!getSdkCallableMethod(currentSdk.app, 'getUpdateState', 'app.getUpdateState');
  return hasCheckForUpdates && hasGetUpdateState;
}
async function requestRequiredRuntimeUpdateCheck(currentSdk: NonNullable<typeof sdk>): Promise<boolean> {
  const checkForUpdates = getSdkCallableMethod(currentSdk.app, 'checkForUpdates', 'app.checkForUpdates');
  if (!checkForUpdates) {
    return false;
  }
  try {
    await Promise.resolve(checkForUpdates.call(currentSdk.app, false));
    return true;
  } catch (error) {
    console.warn('[App] SDK compatibility: failed to trigger required update check', error);
    return false;
  }
}
function shouldContinueWaitingForRequiredUpdate(state: UpdaterState): boolean {
  return state.phase === 'checking' || state.phase === 'available' || state.phase === 'downloading' || state.phase === 'readyToInstall';
}
const sleep = async (ms: number): Promise<void> => {
  await new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });
};
async function resolveManualUpdateFallbackReason(currentSdk: NonNullable<typeof sdk>): Promise<SdkCompatibilityReason | null> {
  const mapStateToFallbackReason = (state: UpdaterState): SdkCompatibilityReason => {
    if (state.phase === 'error') {
      return 'updaterReportedError';
    }
    if (state.phase === 'idle') {
      return 'noCompatibleUpdateFound';
    }
    return 'updaterUnavailable';
  };
  const getUpdateState = getSdkCallableMethod(currentSdk.app, 'getUpdateState', 'app.getUpdateState');
  if (!getUpdateState) {
    return 'updaterUnavailable';
  }
  try {
    const firstState = (await Promise.resolve(getUpdateState.call(currentSdk.app))) as UpdaterState;
    applyUpdaterStateWithToasts(firstState);
    reconcileSdkCompatibilityWithUpdaterState(firstState);
    if (shouldContinueWaitingForRequiredUpdate(firstState)) {
      return null;
    }
  } catch (error) {
    console.warn('[App] SDK compatibility: failed while checking updater progress', error);
    return 'updaterFailed';
  }
  await sleep(REQUIRED_UPDATE_PROGRESS_TIMEOUT_MS);
  try {
    const nextState = (await Promise.resolve(getUpdateState.call(currentSdk.app))) as UpdaterState;
    applyUpdaterStateWithToasts(nextState);
    reconcileSdkCompatibilityWithUpdaterState(nextState);
    if (shouldContinueWaitingForRequiredUpdate(nextState)) {
      return null;
    }
    return mapStateToFallbackReason(nextState);
  } catch (error) {
    console.warn('[App] SDK compatibility: failed while re-checking updater progress', error);
    return 'updaterFailed';
  }
}
function removeGlobalSdkListeners(): void {
  globalRemoveLoadingOverlayListener?.();
  globalRemoveDeepLinkListener?.();
  globalRemoveLogListener?.();
  globalRemoveWarnListener?.();
  globalRemoveErrorListener?.();
  globalRemovePlayModeLogListener?.();
  globalRemovePlayModeWarnListener?.();
  globalRemovePlayModeErrorListener?.();
  globalRemovePlayModeFatalErrorListener?.();
  globalRemovePlayModeFatalOverlayDismissedListener?.();
  globalRemovePlayModeWindowClosedListener?.();
  globalRemoveUpdaterStateListener?.();
  globalRemoveProjectOpenListener?.();
  globalRemoveLoadingOverlayListener = null;
  globalRemoveDeepLinkListener = null;
  globalRemoveLogListener = null;
  globalRemoveWarnListener = null;
  globalRemoveErrorListener = null;
  globalRemovePlayModeLogListener = null;
  globalRemovePlayModeWarnListener = null;
  globalRemovePlayModeErrorListener = null;
  globalRemovePlayModeFatalErrorListener = null;
  globalRemovePlayModeFatalOverlayDismissedListener = null;
  globalRemovePlayModeWindowClosedListener = null;
  globalRemoveUpdaterStateListener = null;
  globalRemoveProjectOpenListener = null;
}
function registerGlobalSdkListeners(currentSdk: NonNullable<typeof sdk>): void {
  removeGlobalSdkListeners();
  ensureSdkStartupCapabilities(currentSdk);
  let hasUnexpectedRegistrationError = false;
  const registerListener = (assign: (unsubscribe: ListenerUnsubscribe | null) => void, owner: unknown, methodName: string, featurePath: string, callback: ListenerCallback) => {
    try {
      assign(registerListenerIfAvailable(owner, methodName, featurePath, callback));
    } catch (error) {
      hasUnexpectedRegistrationError = true;
      console.warn(`[App] SDK compatibility: failed to register ${featurePath}, continuing in degraded mode`, error);
    }
  };
  registerListener(unsubscribe => {
    globalRemoveLoadingOverlayListener = unsubscribe;
  }, currentSdk.ui, 'onLoadingOverlayChange', 'ui.onLoadingOverlayChange', handleLoadingOverlayChange as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemoveDeepLinkListener = unsubscribe;
  }, currentSdk.deepLink, 'onDeepLink', 'deepLink.onDeepLink', handleDeepLink as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemoveProjectOpenListener = unsubscribe;
  }, currentSdk.projectOpen, 'onRequest', 'projectOpen.onRequest', handleProjectOpenRequest as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemoveLogListener = unsubscribe;
  }, currentSdk.logging, 'onLog', 'logging.onLog', handleLog as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemoveWarnListener = unsubscribe;
  }, currentSdk.logging, 'onWarn', 'logging.onWarn', handleWarn as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemoveErrorListener = unsubscribe;
  }, currentSdk.logging, 'onError', 'logging.onError', handleError as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemovePlayModeLogListener = unsubscribe;
  }, currentSdk.playMode, 'onLog', 'playMode.onLog', handlePlayLog as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemovePlayModeWarnListener = unsubscribe;
  }, currentSdk.playMode, 'onWarn', 'playMode.onWarn', handlePlayWarn as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemovePlayModeErrorListener = unsubscribe;
  }, currentSdk.playMode, 'onError', 'playMode.onError', handlePlayError as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemovePlayModeFatalErrorListener = unsubscribe;
  }, currentSdk.playMode, 'onFatalError', 'playMode.onFatalError', handlePlayFatalError as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemovePlayModeFatalOverlayDismissedListener = unsubscribe;
  }, currentSdk.playMode, 'onFatalOverlayDismissed', 'playMode.onFatalOverlayDismissed', handlePlayFatalOverlayDismissed as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemovePlayModeWindowClosedListener = unsubscribe;
  }, currentSdk.playMode, 'onWindowClosed', 'playMode.onWindowClosed', handlePlayWindowClosed as ListenerCallback);
  registerListener(unsubscribe => {
    globalRemoveUpdaterStateListener = unsubscribe;
  }, currentSdk.app, 'onUpdateStateChange', 'app.onUpdateStateChange', handleUpdaterStateChange as ListenerCallback);
  if (hasUnexpectedRegistrationError) {
    removeGlobalSdkListeners();
  }
}

/** Global initialization manager that ensures SDK initialization happens only once */
const globalInitManager = {
  /** Initialize the SDK globally (only once) */
  async initialize(): Promise<void> {
    if (isGloballyInitialized) {
      console.log('[App] SDK already initialized');
      return;
    }
    if (isInitializing && initPromise) {
      console.log('[App] SDK initialization in progress, waiting...');
      return initPromise;
    }
    isInitializing = true;
    console.log('[App] SDK initialization starting...');
    initPromise = (async () => {
      try {
        const {
          sdk,
          appInfo,
          platform,
          lastOpenedProjectPath
        } = await initSdk();
        if (sdk) {
          console.log(`[App] ${PRODUCT_NAME} AI SDK initialized`, {
            ...appInfo,
            platform,
            lastOpenedProjectPath
          });
          installEditorBridge();

          // Check SDK app + bundled engine version compatibility
          let requiresForcedUpdateWait = false;
          if (appInfo?.appVersion) {
            currentSdkVersion = appInfo.appVersion;
            currentSdkEngineVersion = appInfo.engineVersion ?? '';
            requestSentryNextjsSdkVersionTag(appInfo.appVersion);
            const compatibility = getRuntimeCompatibility(appInfo.appVersion, appInfo.engineVersion);
            let compatibilityMode = compatibility.mode;
            let compatibilityReason: SdkCompatibilityReason = 'none';
            isVersionValid = compatibilityMode === 'compatible';
            if (compatibilityMode !== 'compatible') {
              if (compatibility.failureKind === 'engineVersion') {
                compatibilityReason = compatibilityMode === 'compatibilityUnknown' ? 'engineVersionUnknown' : 'engineVersionTooOld';
              } else if (compatibility.failureKind === 'sdkVersion') {
                compatibilityReason = compatibilityMode === 'manualUpdateRequired' ? 'belowManualFloor' : compatibilityMode === 'updateRequiredWaiting' ? 'runtimeVersionTooOld' : 'unknownVersion';
              } else {
                compatibilityReason = 'unknownVersion';
              }
            }
            if (SKIP_SDK_VERSION_CHECK && appInfo.environment === 'dev' && compatibilityMode !== 'compatible') {
              console.warn('[App] NEXT_PUBLIC_SKIP_SDK_VERSION_CHECK is set: skipping SDK and engine version gates', {
                currentSdk: appInfo.appVersion,
                currentEngine: appInfo.engineVersion,
                requiredSdk: MINIMUM_SDK_VERSION,
                requiredEngine: ENGINE_VERSION,
                originalMode: compatibilityMode,
                originalReason: compatibilityReason
              });
              compatibilityMode = 'compatible';
              compatibilityReason = 'none';
              isVersionValid = true;
            }
            if (compatibilityMode === 'compatible') {
              setSdkCompatibilityState('compatible', 'none');
              versionCheckCallbacks.forEach(callback => callback(true, appInfo.appVersion));
            } else if (compatibilityMode === 'manualUpdateRequired') {
              console.warn('[App] Runtime version is too old for in-app recovery:', {
                currentSdk: appInfo.appVersion,
                currentEngine: appInfo.engineVersion,
                minimumAutoUpdate: MINIMUM_SDK_AUTO_UPDATE_VERSION,
                requiredSdk: MINIMUM_SDK_VERSION,
                requiredEngine: ENGINE_VERSION,
                reason: compatibilityReason
              });
              setSdkCompatibilityState('manualUpdateRequired', compatibilityReason);
              versionCheckCallbacks.forEach(callback => callback(false, appInfo.appVersion));
              isGloballyInitialized = true;
              isInitializing = false;
              initCallbacks.forEach(callback => callback(true));
              console.log('[App] Global SDK initialization completed (manual update required)');
              return;
            } else if (compatibilityMode === 'updateRequiredWaiting') {
              console.warn('[App] Runtime version is outdated and requires in-app update before entering editor:', {
                currentSdk: appInfo.appVersion,
                currentEngine: appInfo.engineVersion,
                minimumAutoUpdate: MINIMUM_SDK_AUTO_UPDATE_VERSION,
                requiredSdk: MINIMUM_SDK_VERSION,
                requiredEngine: ENGINE_VERSION,
                reason: compatibilityReason
              });
              setSdkCompatibilityState('updateRequiredWaiting', compatibilityReason);
              versionCheckCallbacks.forEach(callback => callback(false, appInfo.appVersion));
              requiresForcedUpdateWait = true;
            } else {
              console.warn('[App] Runtime version could not be evaluated reliably:', {
                currentSdk: appInfo.appVersion,
                currentEngine: appInfo.engineVersion,
                minimumAutoUpdate: MINIMUM_SDK_AUTO_UPDATE_VERSION,
                requiredSdk: MINIMUM_SDK_VERSION,
                requiredEngine: ENGINE_VERSION,
                reason: compatibilityReason
              });
              setSdkCompatibilityState('manualUpdateRequired', compatibilityReason);
              versionCheckCallbacks.forEach(callback => callback(false, appInfo.appVersion));
              isGloballyInitialized = true;
              isInitializing = false;
              initCallbacks.forEach(callback => callback(true));
              console.log('[App] Global SDK initialization completed (manual update required)');
              return;
            }
          } else {
            setSdkCompatibilityState('compatibilityUnknown', 'unknownVersion');
          }
          registerGlobalSdkListeners(sdk);
          await appPreferencesStore.load();
          await syncUpdaterStateFromSdkSafely(sdk);
          if (requiresForcedUpdateWait) {
            if (!hasRequiredAutoUpdateCapabilities(sdk)) {
              setSdkCompatibilityState('manualUpdateRequired', 'updaterUnavailable');
              isGloballyInitialized = true;
              isInitializing = false;
              initCallbacks.forEach(callback => callback(true));
              console.log('[App] Global SDK initialization completed (manual update fallback)');
              return;
            }
            const updateCheckTriggered = await requestRequiredRuntimeUpdateCheck(sdk);
            if (!updateCheckTriggered) {
              setSdkCompatibilityState('manualUpdateRequired', 'updaterFailed');
              isGloballyInitialized = true;
              isInitializing = false;
              initCallbacks.forEach(callback => callback(true));
              console.log('[App] Global SDK initialization completed (manual update fallback)');
              return;
            }
            const fallbackReason = await resolveManualUpdateFallbackReason(sdk);
            if (fallbackReason) {
              setSdkCompatibilityState('manualUpdateRequired', fallbackReason);
            }
            isGloballyInitialized = true;
            isInitializing = false;
            initCallbacks.forEach(callback => callback(true));
            console.log('[App] Global SDK initialization completed (update gate active)');
            return;
          }
          if (lastOpenedProjectPath) {
            const verifyResult = await verifyProjectPath(lastOpenedProjectPath);
            if (verifyResult) {
              project.setProjectPath(verifyResult.folder, verifyResult.file ?? '');
            } else {
              console.log('[App] Last opened project path is invalid, refreshing projects...');
              project.setProjectPath('', '');
              await project.refreshProjects();
              const firstProject = project.availableProjects.find(p => p.isFound);
              project.setProjectPath(firstProject?.folder ?? '', firstProject?.file ?? '');
            }
          } else {
            project.setProjectPath('', '');
          }
        } else {
          console.log(`[App] ${PRODUCT_NAME} AI Web initialized globally`);
          setSdkCompatibilityState('compatible', 'none');
          await appPreferencesStore.load();
        }
        isGloballyInitialized = true;
        isInitializing = false;

        // Notify all subscribers
        initCallbacks.forEach(callback => callback(true));
        console.log('[App] Global SDK initialization completed');
      } catch (error) {
        console.error('[App] Global SDK initialization failed:', error);
        isInitializing = false;
        initCallbacks.forEach(callback => callback(false));
        throw error;
      }
    })();
    return initPromise;
  },
  /** Subscribe to initialization state changes */
  subscribe(callback: (isInitialized: boolean) => void): () => void {
    initCallbacks.add(callback);

    // If already initialized, call immediately
    if (isGloballyInitialized) {
      callback(true);
    }
    return () => {
      initCallbacks.delete(callback);
    };
  },
  /** Get current initialization state */
  isInitialized(): boolean {
    return isGloballyInitialized;
  },
  /** Subscribe to version check state changes */
  subscribeToVersionCheck(callback: (isVersionValid: boolean, currentVersion: string) => void): () => void {
    versionCheckCallbacks.add(callback);

    // If already checked, call immediately
    if (currentSdkVersion) {
      callback(isVersionValid, currentSdkVersion);
    }
    return () => {
      versionCheckCallbacks.delete(callback);
    };
  },
  /** Get current version check state */
  getVersionState(): {
    isVersionValid: boolean;
    currentVersion: string;
  } {
    return {
      isVersionValid,
      currentVersion: currentSdkVersion
    };
  },
  /** Subscribe to compatibility mode changes */
  subscribeToCompatibility(callback: (mode: SdkCompatibilityMode, reason: SdkCompatibilityReason) => void): () => void {
    sdkCompatibilityCallbacks.add(callback);
    callback(sdkCompatibilityMode, sdkCompatibilityReason);
    return () => {
      sdkCompatibilityCallbacks.delete(callback);
    };
  },
  /** Get current compatibility mode state */
  getCompatibilityState(): {
    mode: SdkCompatibilityMode;
    reason: SdkCompatibilityReason;
  } {
    return {
      mode: sdkCompatibilityMode,
      reason: sdkCompatibilityReason
    };
  },
  /** Cleanup all global listeners (for testing or app shutdown) */
  cleanup(): void {
    removeGlobalSdkListeners();
    stopUpdaterStatePolling();
    isGloballyInitialized = false;
    isInitializing = false;
    initPromise = null;
    initCallbacks.clear();
    isVersionValid = true;
    currentSdkVersion = '';
    currentSdkEngineVersion = '';
    versionCheckCallbacks.clear();
    sdkCompatibilityMode = 'compatible';
    sdkCompatibilityReason = 'none';
    sdkCompatibilityCallbacks.clear();
    console.log('[App] 🧹 Global SDK cleanup completed');
  }
};

/** App entry point hook that initializes the Genesys AI SDK or web app globally only once */
export const useInitApp = () => {
  const $ = _c(12);
  const [isInitialized, setIsInitialized] = useState(_temp);
  const [versionState, setVersionState] = useState(_temp2);
  const [compatibilityState, setCompatibilityState] = useState(_temp3);
  const {
    safeNavigate
  } = useSafeNavigation();
  let t0;
  let t1;
  if ($[0] !== safeNavigate) {
    t0 = () => {
      latestSafeNavigate = safeNavigate;
      console.log("[App] useInitApp hook mounted");
      const unsubscribeInit = globalInitManager.subscribe(setIsInitialized);
      const unsubscribeVersion = globalInitManager.subscribeToVersionCheck((isVersionValid, currentVersion) => {
        setVersionState({
          isVersionValid,
          currentVersion
        });
      });
      const unsubscribeCompatibility = globalInitManager.subscribeToCompatibility((mode, reason) => {
        setCompatibilityState({
          mode,
          reason
        });
      });
      if (!globalInitManager.isInitialized()) {
        globalInitManager.initialize().catch(error => {
          console.error("[App] \u274C Failed to initialize SDK:", error);
          setIsInitialized(false);
        });
      }
      return () => {
        unsubscribeInit();
        unsubscribeVersion();
        unsubscribeCompatibility();
      };
    };
    t1 = [safeNavigate];
    $[0] = safeNavigate;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== isInitialized) {
    t2 = () => {
      if (!isInitialized || !sdk) {
        stopUpdaterStatePolling();
        return;
      }
      const dispose = reaction(_temp4, _temp5, {
        fireImmediately: true
      });
      return () => {
        dispose();
        stopUpdaterStatePolling();
      };
    };
    t3 = [isInitialized];
    $[3] = isInitialized;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== compatibilityState.mode || $[7] !== compatibilityState.reason || $[8] !== isInitialized || $[9] !== versionState.currentVersion || $[10] !== versionState.isVersionValid) {
    t4 = {
      isInitialized,
      isVersionValid: versionState.isVersionValid,
      currentSdkVersion: versionState.currentVersion,
      currentSdkEngineVersion,
      sdkCompatibilityMode: compatibilityState.mode,
      sdkCompatibilityReason: compatibilityState.reason
    };
    $[6] = compatibilityState.mode;
    $[7] = compatibilityState.reason;
    $[8] = isInitialized;
    $[9] = versionState.currentVersion;
    $[10] = versionState.isVersionValid;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
useInitApp.displayName = 'useSdkInitialization';
const hot = (import.meta as ImportMeta & {
  hot?: {
    dispose: (callback: () => void) => void;
  };
}).hot;
if (hot) {
  hot.dispose(() => {
    globalInitManager.cleanup();
  });
}
export async function requestManualUpdateCheck(): Promise<void> {
  if (globalStore.isUpdaterMockEnabled) {
    toast.info('Disable updater mock (Help → Updater mock) to run a real update check.');
    return;
  }
  if (!sdk) {
    return;
  }
  const checkForUpdates = getSdkCallableMethod(sdk.app, 'checkForUpdates', 'app.checkForUpdates');
  if (!checkForUpdates) {
    return;
  }
  try {
    await Promise.resolve(checkForUpdates.call(sdk.app, true));
  } catch (error) {
    console.warn('[App] Failed to check for updates:', error);
    toast.error('Unable to check for updates right now.');
  }
}
export async function requestInstallUpdate(): Promise<void> {
  if (globalStore.isUpdaterMockEnabled) {
    toast.info('Disable updater mock to install a real downloaded update.');
    return;
  }
  if (!sdk) {
    return;
  }
  const quitAndInstallUpdate = getSdkCallableMethod(sdk.app, 'quitAndInstallUpdate', 'app.quitAndInstallUpdate');
  if (!quitAndInstallUpdate) {
    return;
  }
  try {
    await Promise.resolve(quitAndInstallUpdate.call(sdk.app));
  } catch (error) {
    console.warn('[App] Failed to install update:', error);
    toast.error('Unable to install the update right now.');
  }
}

/** Re-apply live SDK updater state after disabling dev mock mode. */
export async function resyncUpdaterStateFromSdk(): Promise<void> {
  if (!sdk) {
    return;
  }
  await syncUpdaterStateFromSdkSafely(sdk);
}

/** Handle deep link events from SDK */
const handleDeepLink = (url: string) => {
  console.log('[App] 🔗 Deep link received from sdk', {
    url
  });
  try {
    if (!latestSafeNavigate) {
      console.warn('[App] No navigation handler available for deep link');
      return;
    }
    const safeNavigate = latestSafeNavigate;
    let targetPath: string;

    // Check if it's a relative path (starts with /)
    if (url.startsWith('/')) {
      targetPath = url;
      console.log('[App] ✅ Navigating to relative deep link path:', targetPath);
      safeNavigate(targetPath);
      return;
    }

    // Try to parse as URL for absolute URLs or custom protocols
    let deepLinkUrl: URL;
    try {
      deepLinkUrl = new URL(url);
    } catch {
      // If URL parsing fails, treat as relative path
      targetPath = url.startsWith('/') ? url : `/${url}`;
      console.log('[App] Navigating to fallback deep link path:', targetPath);
      safeNavigate(targetPath);
      return;
    }
    const currentUrl = new URL(window.location.href);
    const fullUrl = !!deepLinkUrl.origin && deepLinkUrl.origin !== 'null';

    // Handle custom protocols (like genesys-dev://test)
    if (deepLinkUrl.protocol !== 'http:' && deepLinkUrl.protocol !== 'https:') {
      // For custom protocols, the path is typically in the hostname part
      targetPath = deepLinkUrl.hostname || deepLinkUrl.pathname;
      // Ensure it starts with /
      if (!targetPath.startsWith('/')) {
        targetPath = `/${targetPath}`;
      }
      console.log('[App] Navigating to custom protocol deep link path:', targetPath);
      safeNavigate(targetPath);
      return;
    }

    // Verify the origin matches for HTTP/HTTPS URLs to prevent malicious redirects
    if (fullUrl && deepLinkUrl.origin !== currentUrl.origin) {
      console.warn('[App] 🚫 Deep link origin mismatch, ignoring:', {
        deepLinkOrigin: deepLinkUrl.origin,
        currentOrigin: currentUrl.origin
      });
      return;
    }

    // Extract the path and navigate safely
    targetPath = deepLinkUrl.pathname + deepLinkUrl.search + deepLinkUrl.hash;
    console.log('[App] Navigating to safe deep link path:', targetPath);
    safeNavigate(targetPath);
  } catch (error) {
    console.error('[App] Failed to handle deep link:', error);
  }
};
async function reportProjectOpenResult(requestId: string, success: boolean, errorMessage?: string): Promise<void> {
  if (!sdk) {
    return;
  }
  const reportResult = getSdkCallableMethod(sdk.projectOpen, 'reportResult', 'projectOpen.reportResult');
  if (!reportResult) {
    return;
  }
  try {
    await Promise.resolve(reportResult.call(sdk.projectOpen, {
      requestId,
      success,
      errorMessage
    }));
  } catch (error) {
    console.warn('[App] Failed to report project-open result to SDK:', error);
  }
}
async function processQueuedProjectOpenRequests(): Promise<void> {
  if (projectOpenProcessing) {
    return;
  }
  projectOpenProcessing = true;
  try {
    while (queuedProjectOpenRequest) {
      const request = queuedProjectOpenRequest;
      queuedProjectOpenRequest = null;
      console.log('[App] 📂 External project-open request received', request);
      const navigate = latestSafeNavigate;
      if (!navigate) {
        await reportProjectOpenResult(request.requestId, false, 'Navigation is not ready yet.');
        continue;
      }
      const openResult = await handleExternalProjectOpen(request.projectPath);
      if (!openResult.success) {
        await reportProjectOpenResult(request.requestId, false, openResult.errorMessage ?? 'Failed to open project.');
        continue;
      }
      try {
        await navigate('/editor');
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        await reportProjectOpenResult(request.requestId, false, `Opened project but failed to navigate to editor: ${message}`);
        continue;
      }
      await reportProjectOpenResult(request.requestId, true);
    }
  } finally {
    projectOpenProcessing = false;
  }
}
const handleProjectOpenRequest = (request: ProjectOpenRequest) => {
  queuedProjectOpenRequest = request;
  void processQueuedProjectOpenRequests();
};

/** Handle loading overlay changes from SDK */
const handleLoadingOverlayChange = (isVisible: boolean, message?: string, sdkOptions?: {
  replace?: boolean;
}) => {
  if (isVisible) {
    globalStore.showLoadingOverlay('sdk', message ? {
      message,
      ...(sdkOptions?.replace ? {
        replace: true
      } : {})
    } : {});
  } else {
    globalStore.hideLoadingOverlay('sdk');
  }
};

/** Handle log events from SDK */
const handleLog = (...data: any[]) => {
  console.log('[SDK] Log:', ...data);
};

/** Handle warning events from SDK */
const handleWarn = (...data: any[]) => {
  console.warn('[SDK] Warning:', ...data);
};

/** Handle error events from SDK */
const handleError = (...data: any[]) => {
  console.error('[SDK] Error:', ...data);
};

/** Handle log events from play mode (forwarded from play-in-editor page) */
const handlePlayLog = (...data: any[]) => {
  console.log('[Play]', ...data);
};

/** Handle warning events from play mode */
const handlePlayWarn = (...data: any[]) => {
  console.warn('[Play]', ...data);
};

/** Handle error events from play mode */
const handlePlayError = (...data: any[]) => {
  const message = data.length === 1 && typeof data[0] === 'string' ? cleanStackTrace(data[0].trim()) : formatPlayMessageArgs(data);
  if (message) {
    // Keep the forwarded stack text intact instead of creating a new local error stack.
    console.error('[Play][Error]', message);
    globalStore.addPlayModeError(message);
    return;
  }
  console.error('[Play][Error]', ...data);
};

/** Handle unrecoverable play mode errors (startup/crash) */
const handlePlayFatalError = () => {
  console.log('[Play] Fatal signal received');
  globalStore.markPlayModeFatal();
};

/** Exit play mode when a fatal overlay is dismissed inside the play view */
const handlePlayFatalOverlayDismissed = () => {
  if (isHandlingFatalOverlayDismiss) {
    return;
  }
  isHandlingFatalOverlayDismiss = true;
  globalStore.setSuppressNextPlayModeOverlay(true);
  void setPlayModeActive(false).finally(() => {
    isHandlingFatalOverlayDismiss = false;
  });
};

/** Exit play mode when the primary standalone play window is closed manually. */
const handlePlayWindowClosed = () => {
  if (!sceneStore.isPlayMode || sceneStore.isPlayModeStarting) {
    return;
  }
  globalStore.setSuppressNextPlayModeOverlay(true);
  void setPlayModeActive(false);
};
function formatPlayMessageArgs(args: unknown[]): string {
  const mappedArgs = args.map(arg => {
    if (arg instanceof Error) {
      return arg.stack ?? arg.message;
    }
    if (typeof arg === 'string') {
      return arg;
    }
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  });
  return cleanStackTrace(mappedArgs.join(' ').trim());
}
const handleUpdaterStateChange = (nextState: UpdaterState) => {
  if (globalStore.isUpdaterMockEnabled) {
    return;
  }
  applyUpdaterStateWithToasts(nextState);
  reconcileSdkCompatibilityWithUpdaterState(nextState);
};
function _temp() {
  return globalInitManager.isInitialized();
}
function _temp2() {
  return globalInitManager.getVersionState();
}
function _temp3() {
  return globalInitManager.getCompatibilityState();
}
function _temp4() {
  return {
    mock: globalStore.isUpdaterMockEnabled,
    phase: globalStore.updaterPhase
  };
}
function _temp5() {
  ensureUpdaterStatePollingForSdk(sdk);
}